import type { MediaTranscript, MediaTranscriptSegment, MediaTranscriptWord } from '@/types/storage'
import type { AudioItem, TimelineItem, VideoItem } from '@/types/timeline'
import { mediaTranscriptionService } from '../deps/media-transcription-service'
import { useTimelineItemOverlayStore } from '../stores/timeline-item-overlay-store'
import { useTimelineStore } from '../stores/timeline-store'
import { createLogger } from '@/shared/logging/logger'
import type { SilenceRange, SilenceRangesByItemId } from './silence-removal-preview'

export interface FillerRemovalSettings {
  fillerWords: string[]
  fillerPhrases: string[]
  paddingMs: number
  maxSegmentMs: number
}

export type FillerAudioConfidenceLevel = 'high' | 'medium' | 'low' | 'unknown'

export interface FillerAudioConfidence {
  level: FillerAudioConfidenceLevel
  score: number
  fillerScore: number
  nonFillerScore: number
  label: string
}

export type FillerRemovalPresetKey = 'conservative' | 'balanced' | 'aggressive'

export interface FillerRemovalPreset {
  key: FillerRemovalPresetKey
  label: string
  description: string
  settings: FillerRemovalSettings
}

export interface FillerRange extends SilenceRange {
  text: string
  contextBefore?: string
  contextAfter?: string
  sourceStartSec?: number
  sourceEndSec?: number
  audioConfidence?: FillerAudioConfidence
}

export type FillerRangesByItemId = Record<string, FillerRange[]>

export interface FillerPreviewSummaryItem {
  itemId: string
  rangeCount: number
  durationFrames: number
  durationSec: number
}

export interface FillerPreviewSummary {
  items: FillerPreviewSummaryItem[]
  itemCount: number
  itemsWithFillers: number
  totalRanges: number
  totalDurationFrames: number
  totalDurationSec: number
}

export const DEFAULT_SIMPLE_FILLER_WORDS = [
  '嗯',
  '啊',
  '呃',
  '额',
  '唉',
  '哎',
  '欸',
  '诶',
  '哦',
  '噢',
  '恩',
] as const

export const DEFAULT_FILLER_PHRASES = ['怎么说呢', '你知道吧', '我的意思是'] as const

export const SUGGESTED_EXTRA_FILLER_WORDS = ['就是', '然后', '那个', '这个', '其实', '然后呢'] as const

export const DEFAULT_FILLER_REMOVAL_SETTINGS: FillerRemovalSettings = {
  fillerWords: [...DEFAULT_SIMPLE_FILLER_WORDS],
  fillerPhrases: [...DEFAULT_FILLER_PHRASES],
  paddingMs: 35,
  maxSegmentMs: 1800,
}

export const FILLER_REMOVAL_PRESETS: readonly FillerRemovalPreset[] = [
  {
    key: 'conservative',
    label: '保守',
    description: '只抓明显短停顿，尽量减少误伤。',
    settings: {
      fillerWords: [...DEFAULT_SIMPLE_FILLER_WORDS],
      fillerPhrases: [...DEFAULT_FILLER_PHRASES],
      paddingMs: 20,
      maxSegmentMs: 900,
    },
  },
  {
    key: 'balanced',
    label: '平衡',
    description: '默认建议，兼顾召回率和误检控制。',
    settings: DEFAULT_FILLER_REMOVAL_SETTINGS,
  },
  {
    key: 'aggressive',
    label: '激进',
    description: '扩大词库和时长范围，适合先粗筛再手工取消。',
    settings: {
      fillerWords: [...DEFAULT_SIMPLE_FILLER_WORDS, ...SUGGESTED_EXTRA_FILLER_WORDS],
      fillerPhrases: [...DEFAULT_FILLER_PHRASES],
      paddingMs: 55,
      maxSegmentMs: 2400,
    },
  },
] as const

export const FILLER_REMOVAL_ANALYZING_OVERLAY_ID = 'filler-removal-analyzing'
export const FILLER_REMOVAL_PREVIEW_OVERLAY_ID = 'filler-removal-preview'

const logger = createLogger('FillerWordRemovalPreview')

function isFillerRemovableItem(item: TimelineItem): item is AudioItem | VideoItem {
  return (item.type === 'audio' || item.type === 'video') && !!item.mediaId
}

function normalizeWord(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '')
}

function canonicalFillerWord(text: string): string {
  const normalized = normalizeWord(text)
  if (/^u+h+$/.test(normalized)) return 'uh'
  if (/^u+m+$/.test(normalized)) return 'um'
  if (/^u+h+m+$/.test(normalized)) return 'uhm'
  if (/^a+h+$/.test(normalized)) return 'ah'
  if (/^h+m+$/.test(normalized)) return 'hmm'
  if (/^m+$/.test(normalized)) return normalized.length > 1 ? 'mm' : 'm'
  return normalized
}

function normalizePhrase(text: string): string {
  return text
    .split(/\s+/)
    .map(canonicalFillerWord)
    .filter(Boolean)
    .join(' ')
}

function normalizeSegmentTokens(text: string): string[] {
  return text
    .split(/\s+/)
    .map(canonicalFillerWord)
    .filter(Boolean)
}

function collectTranscriptWords(transcript: MediaTranscript): MediaTranscriptWord[] {
  return transcript.segments
    .flatMap((segment) => segment.words ?? [])
    .filter((word) => word.end > word.start && word.text.trim().length > 0)
    .toSorted((left, right) => left.start - right.start)
}

function getPhraseMatchLength(
  words: readonly MediaTranscriptWord[],
  index: number,
  normalizedWords: readonly string[],
  phraseFillers: readonly string[][],
  maxSegmentMs: number,
): number {
  for (const phrase of phraseFillers) {
    if (index + phrase.length > normalizedWords.length) continue
    const matches = phrase.every((part, offset) => normalizedWords[index + offset] === part)
    if (!matches) continue

    const first = words[index]
    const last = words[index + phrase.length - 1]
    if (!first || !last) continue
    if ((last.end - first.start) * 1000 <= maxSegmentMs) {
      return phrase.length
    }
  }
  return 0
}

function isShortSegment(segment: MediaTranscriptSegment, maxSegmentMs: number): boolean {
  return (segment.end - segment.start) * 1000 <= Math.max(0, maxSegmentMs)
}

function matchesFillerSegment(
  segment: MediaTranscriptSegment,
  fillerWords: ReadonlySet<string>,
  fillerPhrases: ReadonlySet<string>,
): boolean {
  const normalizedPhrase = normalizePhrase(segment.text)
  if (!normalizedPhrase) {
    return false
  }

  if (fillerPhrases.has(normalizedPhrase)) {
    return true
  }

  const tokens = normalizeSegmentTokens(segment.text)
  return tokens.length > 0 && tokens.length <= 3 && tokens.every((token) => fillerWords.has(token))
}

function getItemSourceWindow(item: AudioItem | VideoItem, timelineFps: number): {
  sourceStartSeconds: number
  sourceEndSeconds: number
  speed: number
} {
  const sourceFps = item.sourceFps ?? timelineFps
  const speed = item.speed ?? 1
  const sourceStart = item.sourceStart ?? 0
  const visibleSourceFrames = Math.max(1, Math.round((item.durationInFrames / timelineFps) * sourceFps * speed))
  const boundedSourceEnd = item.sourceEnd ?? sourceStart + visibleSourceFrames

  return {
    sourceStartSeconds: sourceStart / sourceFps,
    sourceEndSeconds: boundedSourceEnd / sourceFps,
    speed,
  }
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function formatFillerOverlayLabel(rangeCount: number, durationSec: number): string {
  if (rangeCount <= 0 || durationSec <= 0) {
    return '未检测到口头禅'
  }

  return `口头禅 ${rangeCount} 段 · ${durationSec.toFixed(durationSec >= 10 ? 0 : 1)}s`
}

function formatContextWords(words: readonly MediaTranscriptWord[]): string {
  return words
    .map((word) => word.text.trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim()
}

function getWordMatchContext(
  words: readonly MediaTranscriptWord[],
  startIndex: number,
  endIndexExclusive: number,
): { contextBefore?: string; contextAfter?: string } {
  const contextRadius = 3
  const contextBefore = formatContextWords(words.slice(Math.max(0, startIndex - contextRadius), startIndex))
  const contextAfter = formatContextWords(words.slice(endIndexExclusive, endIndexExclusive + contextRadius))
  return {
    contextBefore: contextBefore || undefined,
    contextAfter: contextAfter || undefined,
  }
}

export function detectFillerRangesFromTranscriptSegments(
  transcript: MediaTranscript,
  settings: FillerRemovalSettings = DEFAULT_FILLER_REMOVAL_SETTINGS,
): Array<{ startSec: number; endSec: number; text: string; contextBefore?: string; contextAfter?: string }> {
  if (collectTranscriptWords(transcript).length > 0) {
    return detectFillerRangesFromTranscriptWords(transcript, settings)
  }

  const fillerWords = new Set(settings.fillerWords.map(canonicalFillerWord).filter(Boolean))
  const fillerPhrases = new Set(settings.fillerPhrases.map(normalizePhrase).filter(Boolean))
  const paddingSec = Math.max(0, settings.paddingMs) / 1000

  return transcript.segments
    .filter((segment) => segment.end > segment.start)
    .filter((segment) => isShortSegment(segment, settings.maxSegmentMs))
    .filter((segment) => matchesFillerSegment(segment, fillerWords, fillerPhrases))
    .map((segment) => ({
      startSec: Math.max(0, segment.start - paddingSec),
      endSec: segment.end + paddingSec,
      text: segment.text.trim(),
    }))
}

export function detectFillerRangesFromTranscriptWords(
  transcript: MediaTranscript,
  settings: FillerRemovalSettings = DEFAULT_FILLER_REMOVAL_SETTINGS,
): Array<{ startSec: number; endSec: number; text: string; contextBefore?: string; contextAfter?: string }> {
  const words = collectTranscriptWords(transcript)
  if (words.length === 0) {
    return []
  }

  const fillerWords = new Set(settings.fillerWords.map(canonicalFillerWord).filter(Boolean))
  const fillerPhrases = settings.fillerPhrases
    .map((phrase) => phrase.split(/\s+/).map(canonicalFillerWord).filter(Boolean))
    .filter((phrase) => phrase.length > 0)
    .toSorted((left, right) => right.length - left.length)
  const paddingSec = Math.max(0, settings.paddingMs) / 1000
  const normalizedWords = words.map((word) => canonicalFillerWord(word.text))
  const ranges: Array<{ startSec: number; endSec: number; text: string }> = []

  for (let index = 0; index < words.length; index += 1) {
    const word = words[index]
    const normalized = normalizedWords[index]
    if (!word || !normalized) continue

    if (fillerWords.has(normalized) && (word.end - word.start) * 1000 <= settings.maxSegmentMs) {
      ranges.push({
        startSec: Math.max(0, word.start - paddingSec),
        endSec: word.end + paddingSec,
        text: word.text.trim(),
        ...getWordMatchContext(words, index, index + 1),
      })
      continue
    }

    const phraseLength = getPhraseMatchLength(
      words,
      index,
      normalizedWords,
      fillerPhrases,
      settings.maxSegmentMs,
    )
    if (phraseLength === 0) continue

    const first = words[index]
    const last = words[index + phraseLength - 1]
    if (!first || !last) continue
    ranges.push({
      startSec: Math.max(0, first.start - paddingSec),
      endSec: last.end + paddingSec,
      text: words
        .slice(index, index + phraseLength)
        .map((entry) => entry.text.trim())
        .join(' ')
        .trim(),
      ...getWordMatchContext(words, index, index + phraseLength),
    })
    index += phraseLength - 1
  }

  return ranges
}

export async function analyzeFillerWordsForItems(
  itemIds: readonly string[],
  settings: FillerRemovalSettings = DEFAULT_FILLER_REMOVAL_SETTINGS,
): Promise<FillerRangesByItemId> {
  const timeline = useTimelineStore.getState()
  const itemsById = new Map(timeline.items.map((item) => [item.id, item]))
  const results: FillerRangesByItemId = {}
  const analyzableItems: Array<{ itemId: string; item: AudioItem | VideoItem }> = []

  for (const itemId of itemIds) {
    const item = itemsById.get(itemId)
    if (!item || !isFillerRemovableItem(item)) {
      results[itemId] = []
      continue
    }

    analyzableItems.push({ itemId, item })
  }

  const settledResults = await Promise.allSettled(
    analyzableItems.map(async ({ itemId, item }) => {
      const transcript = await mediaTranscriptionService.getTranscript(item.mediaId)
      if (!transcript || transcript.segments.length === 0) {
        throw new Error('请先为选中素材生成转录结果，再使用口头禅移除。')
      }

      const { sourceStartSeconds, sourceEndSeconds, speed } = getItemSourceWindow(item, timeline.fps)
      const clipRanges = detectFillerRangesFromTranscriptSegments(transcript, settings)
        .filter((range) => range.endSec > sourceStartSeconds && range.startSec < sourceEndSeconds)
        .map((range) => {
          const clippedStartSec = clampNumber(range.startSec, sourceStartSeconds, sourceEndSeconds)
          const clippedEndSec = clampNumber(range.endSec, sourceStartSeconds, sourceEndSeconds)
          const localStartSec = Math.max(0, (clippedStartSec - sourceStartSeconds) / speed)
          const localEndSec = Math.max(localStartSec, (clippedEndSec - sourceStartSeconds) / speed)
          const startFrame = clampNumber(Math.floor(localStartSec * timeline.fps), 0, item.durationInFrames)
          const endFrame = clampNumber(Math.ceil(localEndSec * timeline.fps), startFrame, item.durationInFrames)
          const durationFrames = Math.max(0, endFrame - startFrame)

          return {
            startFrame,
            endFrame,
            durationFrames,
            startMs: Math.round(localStartSec * 1000),
            endMs: Math.round(localEndSec * 1000),
            durationMs: Math.max(0, Math.round((localEndSec - localStartSec) * 1000)),
            text: range.text,
            contextBefore: range.contextBefore,
            contextAfter: range.contextAfter,
            sourceStartSec: clippedStartSec,
            sourceEndSec: clippedEndSec,
          } satisfies FillerRange
        })
        .filter((range) => range.endFrame > range.startFrame)

      return { itemId, ranges: clipRanges }
    }),
  )

  let successCount = 0
  for (let index = 0; index < settledResults.length; index++) {
    const settled = settledResults[index]
    const itemId = analyzableItems[index]?.itemId

    if (!settled || !itemId) {
      continue
    }

    if (settled.status === 'fulfilled') {
      successCount += 1
      results[itemId] = settled.value.ranges
      continue
    }

    results[itemId] = []
    logger.warn('Filler word detection failed for item', {
      itemId,
      reason: settled.reason,
    })
  }

  if (analyzableItems.length > 0 && successCount === 0) {
    throw new Error('未找到可用转录结果，请先生成字幕或转录后重试。')
  }

  return results
}

export function applyFillerPreviewOverlays(
  itemIds: readonly string[],
  rangesByItemId: FillerRangesByItemId,
): FillerPreviewSummary {
  const { upsertOverlay, removeOverlay } = useTimelineItemOverlayStore.getState()

  const items = itemIds.map((itemId) => {
    const ranges = rangesByItemId[itemId] ?? []
    const durationFrames = ranges.reduce((sum, range) => sum + range.durationFrames, 0)
    const durationSec = durationFrames / Math.max(1, useTimelineStore.getState().fps)

    upsertOverlay(itemId, {
      id: FILLER_REMOVAL_PREVIEW_OVERLAY_ID,
      label: formatFillerOverlayLabel(ranges.length, durationSec),
      tone: ranges.length > 0 ? 'warning' : 'success',
    })

    removeOverlay(itemId, FILLER_REMOVAL_ANALYZING_OVERLAY_ID)

    return {
      itemId,
      rangeCount: ranges.length,
      durationFrames,
      durationSec,
    }
  })

  return {
    items,
    itemCount: items.length,
    itemsWithFillers: items.filter((item) => item.rangeCount > 0).length,
    totalRanges: items.reduce((sum, item) => sum + item.rangeCount, 0),
    totalDurationFrames: items.reduce((sum, item) => sum + item.durationFrames, 0),
    totalDurationSec: items.reduce((sum, item) => sum + item.durationSec, 0),
  }
}

export function clearFillerRemovalOverlays(itemIds: readonly string[]): void {
  const { removeOverlay } = useTimelineItemOverlayStore.getState()

  for (const itemId of itemIds) {
    removeOverlay(itemId, FILLER_REMOVAL_ANALYZING_OVERLAY_ID)
    removeOverlay(itemId, FILLER_REMOVAL_PREVIEW_OVERLAY_ID)
  }
}

export function asSilenceRangesByItemId(rangesByItemId: FillerRangesByItemId): SilenceRangesByItemId {
  return Object.fromEntries(
    Object.entries(rangesByItemId).map(([itemId, ranges]) => [itemId, ranges]),
  )
}